import React, { useState, useContext } from 'react';
import { useGetProcedureInsightsQuery, useGetHospitalProcedureInsightsQuery } from '../../api/procedureSlice';
import { useParams, useNavigate } from 'react-router';
import { Grid, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Chip, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ResponsiveBoxPlot } from '@nivo/boxplot';
import CardUsageComparison from './CardUsageComparison';
import CategoryBreakdown from './CategoryBreakdown';
import { theme } from '../../theme';
import { UserContext } from '../Hooks/useUser';
import ProcedureSearchDropdown from './ProcedureSearchDropdown';

function ProcedureAnalysis() {
    const procedureId = useParams().procedureId;
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [selectedPhysicians, setSelectedPhysicians] = useState([]);
    const [activeView, setActiveView] = useState('categoryBreakdown');

    const { data: procedures = [] } = useGetHospitalProcedureInsightsQuery(user?.hospital?.id, { skip: !user?.hospital?.id });

    const { data: procedureRollupData, isLoading, isSuccess } = useGetProcedureInsightsQuery(procedureId);
    
    const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
    const fmtNum = (v) => Number(v).toLocaleString('en-US', { maximumFractionDigits: 1 });

    const caseBoxPlotData = React.useMemo(() => {
        if (!procedureRollupData) return [];

        const allCases = procedureRollupData.cases.map((caseData) => ({
            'group': 'All Cases',
            'value': caseData.case_cost / 100,
            'subgroup': 'all_cases',
            'sd': procedureRollupData.case_rollup.stddev_case_cost / 100,
            'n': procedureRollupData.case_rollup.num_cases,
            'mu': procedureRollupData.case_rollup.average_case_cost / 100,
        }));

        const physicianCases = procedureRollupData.cases.map((caseData) => ({
            'group': caseData.physician_name,
            'value': caseData.case_cost / 100,
            'sd': procedureRollupData.case_rollup.stddev_case_cost / 100,
            'n': procedureRollupData.case_rollup.num_cases,
            'mu': procedureRollupData.case_rollup.average_case_cost / 100,
        }));

        const sortedPhysicianData = physicianCases.sort((a, b) => a.mu - b.mu);
        
        return [...allCases, ...sortedPhysicianData];
    }, [procedureRollupData]);

    const selectedPhysicianNames = selectedPhysicians.map(c => c.name);

    const calcMedian = (values) => {
        if (!values.length) return 0;
        const sorted = [...values].map(Number).sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    };

    const medianSpends = React.useMemo(() => {
        if (!procedureRollupData) return [0, 0];
        return selectedPhysicians.map(physician => {
            const costs = procedureRollupData.cases
                .filter(c => c.physician_name === physician.name)
                .map(c => c.case_cost);
            return calcMedian(costs);
        });
    }, [selectedPhysicians, procedureRollupData]);

    async function handleSelectBoxPlot(data) {
        if (data.group === 'All Cases') {
            setSelectedPhysicians([]);
        } else {
            const physician = procedureRollupData.physicians.find(p => p.name === data.group);
            if (physician) {
                setSelectedPhysicians(prev => {
                    const exists = prev.some(p => p.id === physician.id);
                    const newPhysicians = exists 
                        ? prev.filter(p => p.id !== physician.id) // de-select if already selected
                        : [...prev, physician];                   // select if not already selected
                    return newPhysicians.slice(-2);               // keep only last 2 selected
                    // this is diff than how procedure comparison is set up (can't select a third until deselect on of the earlier ones)
                });
            }
        }
    }

    function switchToProcedure(id) {
    
        setSelectedPhysicians([]);

        if (id !== '' && id != null) {
            navigate(`/procedures/${id}/analysis`);
        }
    }

    return (
        <Grid container spacing={2} pb={4}>
            <Grid item xs={12} sx={{ position: 'sticky', top: 64, zIndex: 5, backgroundColor: theme.palette.background.white, borderBottom: '1px solid', borderColor: 'divider' }} position='sticky'>
                <Stack px={3} pb={2}>
                    <Typography variant='bold' fontSize='20px'>Procedure Analysis</Typography>
                    <ProcedureSearchDropdown
                        options={procedures}
                        value={Number(procedureId)}
                        onSelect={(id) => {switchToProcedure(id);}}
                        placeholder='Switch procedure...'
                        sx={{ width: '40%' }}
                    />
                </Stack>
            </Grid>
            {isLoading && <Grid item xs={12}>Loading...</Grid>}
            {isSuccess && procedureRollupData && (
                <Grid container spacing={2} mx={3} mt={1}>
                    {/* ── Procedure Cases Panel ── */}
                    <Grid item xs={12} md={12}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6" fontWeight={700}>Procedure Cases</Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip label={`${procedureRollupData.case_rollup.num_cases} Cases (12 mo)`} color="secondary" variant="outlined" />
                                    <Chip label={`${procedureRollupData.num_distinct_items_used} Distinct SKUs Used`} color="secondary" variant="outlined" />
                                </Box>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Metric</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Min</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: 'secondary.main' }}>Avg</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>Max</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Std Dev</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>SKUs Picked per Case</TableCell>
                                            <TableCell align="right">{fmtNum(procedureRollupData.case_rollup.min_num_items_used)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'secondary.main' }}>{fmtNum(procedureRollupData.case_rollup.average_num_items_used)}</TableCell>
                                            <TableCell align="right">{fmtNum(procedureRollupData.case_rollup.max_num_items_used)}</TableCell>
                                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtNum(procedureRollupData.case_rollup.stddev_num_items_used)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Quantity Used per Case</TableCell>
                                            <TableCell align="right">{fmtNum(procedureRollupData.case_rollup.min_quantity_items_used)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'secondary.main' }}>{fmtNum(procedureRollupData.case_rollup.average_quantity_items_used)}</TableCell>
                                            <TableCell align="right">{fmtNum(procedureRollupData.case_rollup.max_quantity_items_used)}</TableCell>
                                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtNum(procedureRollupData.case_rollup.stddev_quantity_items_used)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Case Cost</TableCell>
                                            <TableCell align="right">{fmt(procedureRollupData.case_rollup.min_case_cost / 100)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'secondary.main' }}>{fmt(procedureRollupData.case_rollup.average_case_cost / 100)}</TableCell>
                                            <TableCell align="right">{fmt(procedureRollupData.case_rollup.max_case_cost / 100)}</TableCell>
                                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmt(procedureRollupData.case_rollup.stddev_case_cost / 100)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: 450 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Case Cost by Physician</Typography>
                            <div style={{ height: 370, width: '100%' }}>
                                <ResponsiveBoxPlot
                                    data={caseBoxPlotData}
                                    minValue={caseBoxPlotData.reduce((min, d) => Math.min(min, d.value), Infinity) - caseBoxPlotData.reduce((min, d) => Math.min(min, d.value), Infinity) * 0.01}
                                    layout='vertical'
                                    margin={{ top: 20, right: 30, bottom: 50, left: 70 }}
                                    xScale={{ type: 'point' }}
                                    yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                                    onClick={(node) => handleSelectBoxPlot(node)}
                                    colorBy="group"
                                    layers={['grid', 'axes', 'boxPlots', 'markers', 'legends', 'annotations']}
                                    tooltip={point => {
                                        return <CustomTooltip datum={point} />;
                                    }}
                                    axisBottom={{
                                        orient: 'bottom',
                                        tickSize: 5,
                                        tickPadding: 5,
                                        tickRotation: -45,
                                        legend: 'Physician Name',
                                        legendOffset: 36,
                                        legendPosition: 'middle',
                                        renderTick: (tick) => {
                                            const isSelected = selectedPhysicianNames.includes(tick.value);
                                            return (
                                                <g transform={`translate(${tick.x},${tick.y})`}>
                                                    <line x1={0} x2={0} y1={0} y2={5} stroke={theme.palette.subtext.secondary} />
                                                    <text
                                                        textAnchor="middle"
                                                        y={18}
                                                        style={{
                                                            fontSize: 11,
                                                            fill: isSelected ? theme.palette.analytics.cardTypes.used : theme.palette.subtext.secondary,
                                                            fontWeight: isSelected ? 700 : 400,
                                                        }}
                                                    >
                                                        {tick.value}
                                                    </text>
                                                    {isSelected && (
                                                        <rect
                                                            x={-20}
                                                            y={22}
                                                            width={40}
                                                            height={3}
                                                            rx={1.5}
                                                            fill={theme.palette.analytics.cardTypes.used}
                                                        />
                                                    )}
                                                </g>
                                            );
                                        }
                                    }}
                                    axisLeft={{
                                        orient: 'left',
                                        tickSize: 5,
                                        tickPadding: 5,
                                        tickRotation: 0,
                                        legend: 'Case Cost',
                                        legendOffset: -60,
                                        legendPosition: 'middle',
                                        tickFormat: (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v)
                                    }}
                                />
                            </div>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                            <ToggleButtonGroup
                                value={activeView}
                                exclusive
                                onChange={(_, val) => val && setActiveView(val)}
                                size="small"
                            >
                                <ToggleButton value="categoryBreakdown">Category Breakdown</ToggleButton>
                                <ToggleButton value="cardUsageComparison">Physician Comparison</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {activeView === 'categoryBreakdown' && <CategoryBreakdown />}
                        {activeView === 'cardUsageComparison' && (
                            <CardUsageComparison
                                selectedPhysicians={selectedPhysicians}
                                medianSpendA={medianSpends[0] ?? 0}
                                medianSpendB={medianSpends[1] ?? 0}
                            />
                        )}
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}

function CustomTooltip({ datum }) {
    return (
        <Box sx={{ backgroundColor: '#fff', padding: 1, borderRadius: 1, boxShadow: 3, width: 300 }}>
            <Typography variant="subtitle2"><Box sx={{ backgroundColor: datum.color, width: 12, height: 12, display: 'inline-block', marginRight: 1 }} />{datum.group} ({datum.data.n} Cases /yr)</Typography>
            <Stack direction='row' spacing={2}>
                <Stack>
                    <Typography variant="body2"><b>Min:</b> {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(datum.data.extrema[0])}</Typography>
                    <Typography variant="body2"><b>Max:</b> {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(datum.data.extrema[1])}</Typography>
                </Stack>
                <Stack>
                    <Typography variant="body2"><b>Median:</b> {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(datum.data.values[2])}</Typography>
                    <Typography variant="body2"><b>Average:</b> {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(datum.data.mean)}</Typography>
                </Stack>
            </Stack>
        </Box>
    );
}


export default ProcedureAnalysis;